
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfWeek, addWeeks, subWeeks } from 'date-fns';
import { WeekVisibility } from '../types';
import { useUser } from '@/contexts/UserContext';
import { useManagerPermissions } from '@/hooks/useManagerPermissions';

export function useWeekVisibility(teamId: string, initialDate: Date = new Date()) {
  const [weekVisibilityMap, setWeekVisibilityMap] = useState<Map<string, boolean>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAdmin, isManager } = useUser();
  const { canManageTeam } = useManagerPermissions(teamId);
  
  // Determine if user has admin-like permissions (admin, manager role, OR manager of this team)
  const hasAdminLikePermissions = isAdmin || isManager || canManageTeam;
  
  // Load week visibility settings for the range around the current date
  const loadWeekVisibility = useCallback(async (centerDate: Date = initialDate) => {
    if (!teamId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Load visibility for a range of weeks (12 weeks around current date)
      const startDate = format(subWeeks(startOfWeek(centerDate, { weekStartsOn: 1 }), 6), 'yyyy-MM-dd');
      const endDate = format(addWeeks(startOfWeek(centerDate, { weekStartsOn: 1 }), 6), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('week_visibility')
        .select('*')
        .eq('team_id', teamId)
        .gte('week_start_date', startDate)
        .lte('week_start_date', endDate);
      
      if (error) {
        throw error;
      }
      
      // Create a map of week start dates to visibility status
      const visibilityMap = new Map<string, boolean>();
      
      if (data) {
        data.forEach((item: any) => {
          visibilityMap.set(item.week_start_date, item.is_visible);
        });
      }
      
      setWeekVisibilityMap(visibilityMap);
      
    } catch (err) {
      console.error('Error loading week visibility:', err);
      setError(err instanceof Error ? err : new Error('Failed to load week visibility'));
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);
  
  // Update visibility for a specific week - now allows managers too
  const updateWeekVisibility = async (date: Date, isVisible: boolean): Promise<boolean> => {
    console.log('updateWeekVisibility called with:', {
      teamId,
      date: date.toISOString(),
      isVisible,
      isAdmin,
      isManager,
      canManageTeam,
      hasAdminLikePermissions
    });
    
    // Check if user has permission to update visibility
    if (!hasAdminLikePermissions) {
      console.log('User does not have permission to update week visibility');
      return false;
    }
    
    const weekStartDate = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    
    try {
      console.log('Updating week visibility in database:', {
        team_id: teamId,
        week_start_date: weekStartDate,
        is_visible: isVisible
      });
      
      // Update database first
      const { error } = await supabase
        .from('week_visibility')
        .upsert({
          team_id: teamId,
          week_start_date: weekStartDate,
          is_visible: isVisible
        }, {
          onConflict: 'team_id,week_start_date'
        });
      
      if (error) {
        console.error('Error updating week visibility in database:', error);
        throw error;
      }
      
      console.log('Successfully updated week visibility in database');
      
      // Update local state after successful database update
      setWeekVisibilityMap(prev => {
        const newMap = new Map(prev);
        newMap.set(weekStartDate, isVisible);
        return newMap;
      });
      
      return true;
    } catch (err) {
      console.error('Error in updateWeekVisibility:', err);
      return false;
    }
  };
  
  // Check if a date is visible - FIXED: Now respects explicit settings even for admins
  const isDateVisible = useCallback((date: Date): boolean => {
    const weekStartDate = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    
    // First, check if there's an explicit visibility setting for this week
    if (weekVisibilityMap.has(weekStartDate)) {
      const explicitSetting = weekVisibilityMap.get(weekStartDate) === true;
      console.log('useWeekVisibility - Explicit setting found:', {
        weekStartDate,
        explicitSetting,
        userRole: hasAdminLikePermissions ? 'admin-like' : 'regular'
      });
      return explicitSetting;
    }
    
    // If no explicit setting exists, use role-based defaults
    const defaultVisible = hasAdminLikePermissions;
    console.log('useWeekVisibility - No explicit setting, using default:', {
      weekStartDate,
      defaultVisible,
      userRole: hasAdminLikePermissions ? 'admin-like' : 'regular'
    });
    return defaultVisible;
  }, [weekVisibilityMap, hasAdminLikePermissions]);
  
  // Initial load of visibility settings
  useEffect(() => {
    if (teamId) {
      loadWeekVisibility();
    }
  }, [teamId, loadWeekVisibility]);
  
  return {
    isLoading,
    error,
    isDateVisible,
    updateWeekVisibility,
    loadWeekVisibility,
    hasAdminLikePermissions
  };
}
